/**
 * Validation Service - Pure utility functions for form and data validation
 * No API calls - only client-side validation logic
 */

/**
 * Check if a value is null, empty, undefined, or "null" string
 * @param {any} val - Value to check
 * @returns {boolean} True if empty/null
 */
export const Check_Null_Empty = (val) => {
    if (val === '' || val === null || val === undefined || val === "null" || val === "{}") {
        return true;
    }
    return false;
};

/**
 * Validate email format
 * @param {string} val - Email to validate
 * @returns {boolean} True if valid email
 */
export const ValidateEmail = (val) => {
    const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRegex.test(val);
};

/**
 * Validate email format (alternative implementation)
 * @param {string} val - Email to validate
 * @returns {boolean} True if valid email
 */
export const ValidateEmailPlus = (val) => {
    const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRegex.test(val);
};

/** US NANP: area code and exchange cannot start with 0 or 1 */
const US_PHONE_10_DIGIT = /^[2-9]\d{2}[2-9]\d{6}$/;

/**
 * Validate US phone number format (10-digit or 11-digit with leading 1)
 * Accepts: (123) 456-7890, 123-456-7890, 123.456.7890, 1234567890, 1-800-555-0100, +1 800 555 0100
 * @param {string} val - Phone number to validate
 * @returns {boolean} True if valid phone number
 */
export const validatePhoneNumber = (val) => {
    if (!val || typeof val !== 'string') return false;
    const trimmed = val.trim();
    const formattedPattern = /^\(?(\d{3})\)?[-.\s]?(\d{3})[-.\s]?(\d{4})$/;
    if (formattedPattern.test(trimmed)) return true;

    const digits = trimmed.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
        return US_PHONE_10_DIGIT.test(digits.slice(1));
    }
    if (digits.length === 10) {
        return US_PHONE_10_DIGIT.test(digits);
    }
    return false;
};

/**
 * Validate phone number (alias — same US rules as validatePhoneNumber)
 * @param {string} elementValue - Phone number to validate
 * @returns {boolean} True if valid phone number
 */
export const validateNumber = (elementValue) => validatePhoneNumber(elementValue);

/**
 * Validate currency amount (allows up to 6 decimal places)
 * @param {string|number} amount - Amount to validate
 * @returns {boolean} True if valid currency format
 */
export const validateCurrency = (amount) => {
    const regex = /^[0-9]\d*(?:\.\d{0,6})?$/;
    return regex.test(amount);
};

/**
 * Check if string has leading or trailing spaces
 * @param {string} string - String to check
 * @returns {boolean} True if has leading/trailing spaces
 */
export const ValidateSpace = (string) => {
    const firstChar = string.charAt(0);
    const lastChar = string.charAt(string.length - 1);
    if (firstChar === ' ' || lastChar === ' ') {
        return true;
    }
    return false;
};

/**
 * Check if value is numeric
 * @param {string|number} val - Value to check
 * @returns {boolean} True if numeric
 */
export const checkNumericValue = (val) => {
    const numericePattern = /^[\+\-]?\d*\.?\d+(?:[Ee][\+\-]?\d+)?$/;
    return numericePattern.test(val);
};

/**
 * Format value with dollar sign or percentage
 * @param {string|number} val - Value to format
 * @param {string} unitsign - Unit sign ('$' or '%')
 * @returns {string} Formatted value
 */
export const AddDollar = (val, unitsign) => {
    if (val === '') {
        return '';
    }
    
    if (unitsign === '$') {
        const removeDollar = String(val).replace('$', '');
        const addDecimal = parseFloat(removeDollar).toFixed(2);
        return '$' + addDecimal;
    } else if (unitsign === '%') {
        const removeDollar = String(val).replace('%', '');
        const addDecimal = parseFloat(removeDollar).toFixed(2);
        return addDecimal + '%';
    } else {
        const removeDollar = String(val).replace('$', '');
        return '$' + parseFloat(removeDollar).toFixed(2);
    }
};

/**
 * Validation service object for compatibility with existing imports
 */
export const validationService = {
    Check_Null_Empty,
    ValidateEmail,
    ValidateEmailPlus,
    validatePhoneNumber,
    validateNumber,
    validateCurrency,
    ValidateSpace,
    checkNumericValue,
    AddDollar,
};

export default validationService;
